import type { AssistantRuntime } from '@assistant-ui/react';
import type { UIMessageChunk } from 'ai';

type ReplayEntry = ReturnType<AssistantRuntime['thread']['export']>['messages'][number];
type Part = ReplayEntry['message']['content'][number];
type CurrentRuntime = () => AssistantRuntime | undefined;

function isRetainedToolPrefix(
  current: Extract<Part, { type: 'tool-call' }>,
  retained: Extract<Part, { type: 'tool-call' }>,
) {
  if (
    current.toolCallId !== retained.toolCallId ||
    current.toolName !== retained.toolName ||
    !retained.argsText.startsWith(current.argsText)
  )
    return false;
  if (current.isError !== undefined && current.isError !== retained.isError) return false;
  if (JSON.stringify(current.approval) !== JSON.stringify(retained.approval)) return false;
  return (
    current.result === undefined ||
    (JSON.stringify(current.result) === JSON.stringify(retained.result) &&
      current.isError === retained.isError)
  );
}

function isRetainedPrefix(current: Part, retained: Part) {
  if (current.type !== retained.type) return false;
  if (current.type === 'text' && retained.type === 'text')
    return retained.text.startsWith(current.text);
  if (current.type === 'reasoning' && retained.type === 'reasoning')
    return retained.text.startsWith(current.text);
  if (current.type === 'tool-call' && retained.type === 'tool-call')
    return isRetainedToolPrefix(current, retained);
  return JSON.stringify(current) === JSON.stringify(retained);
}

function restoreRetained(currentRuntime: CurrentRuntime, threadId: string, retained: ReplayEntry) {
  const runtime = currentRuntime();
  if (!runtime) return;
  const thread = runtime.thread;
  const state = thread.getState();
  if (state.threadId !== threadId) return;
  const current = state.messages.at(-1);
  if (current?.id !== retained.message.id) return;
  if ((state.messages.at(-2)?.id ?? null) !== retained.parentId) return;
  const parts = current.content;
  const original = retained.message.content;
  if (parts.length > original.length) return;
  if (!parts.every((part, index) => isRetainedPrefix(part, original[index]))) return;
  if (JSON.stringify(parts) === JSON.stringify(original)) return;
  const repository = thread.export();
  // The SDK marks a replayed message optimistic: export can omit it while the
  // visible state still contains that exact original. Fence the VISIBLE head,
  // preserve every current exported branch, and restore only this reply slot.
  const messages = repository.messages.filter((entry) => entry.message.id !== current.id);
  thread.import({ ...repository, headId: retained.message.id, messages: [...messages, retained] });
}

function selectReplayBaseline(
  runtime: AssistantRuntime,
  answerId: string,
  currentRuntime: CurrentRuntime,
) {
  const thread = runtime.thread;
  const repository = thread.export();
  const original = repository.messages.find((item) => item.message.id === answerId);
  if (!original) {
    if (thread.getState().messages.at(-1)?.role === 'assistant')
      throw new Error('stale_answer_resume');
    return;
  }
  if (repository.headId !== answerId) throw new Error('stale_answer_resume');
  // Reuse the public SDK history API. Keep the original branch and its ID,
  // but start this full replay from its real parent, so native SDK text-start
  // does not append the already-loaded partial a second time. No DB write.
  const threadId = thread.getState().threadId;
  thread.import({ ...repository, headId: original.parentId });
  let interrupted = false;
  // The native SDK publishes its final replay snapshot asynchronously. Restore
  // after that same run ends, so a queued SDK write cannot erase the recovery.
  const release = thread.unstable_on('runEnd', () => {
    release();
    if (interrupted) queueMicrotask(() => restoreRetained(currentRuntime, threadId, original));
  });
  return () => {
    interrupted = true;
  };
}

/** Runs only after the current authorized GET returns an actual start event.
 * The original POST/network-retry path uses its existing parser/byte cursor.
 * A failed GET keeps the loaded partial; crossed/old answers cannot graft onto
 * a newer head. This never dispatches, approves or stops producer work. */
export async function prepareAnswerReplay(
  stream: ReadableStream<UIMessageChunk>,
  runtime?: AssistantRuntime | CurrentRuntime,
  expectedAnswerId?: string,
) {
  const currentRuntime = typeof runtime === 'function' ? runtime : () => runtime;
  const actual = currentRuntime();
  if (!actual) return stream;
  const reader = stream.getReader();
  let first: ReadableStreamReadResult<UIMessageChunk>;
  let restore: (() => void) | undefined;
  try {
    first = await reader.read();
    if (
      expectedAnswerId &&
      (first.done || first.value.type !== 'start' || first.value.messageId !== expectedAnswerId)
    )
      throw new Error('stale_answer_resume');
    if (!first.done && first.value.type === 'start' && first.value.messageId)
      restore = selectReplayBaseline(actual, first.value.messageId, currentRuntime);
  } catch (error) {
    await reader.cancel(error).catch(() => {});
    reader.releaseLock();
    throw error;
  }
  let initial = true;
  return new ReadableStream<UIMessageChunk>({
    async pull(controller) {
      try {
        const next = initial ? first : await reader.read();
        initial = false;
        if (next.done) {
          reader.releaseLock();
          controller.close();
        } else controller.enqueue(next.value);
      } catch (error) {
        await reader.cancel(error).catch(() => {});
        reader.releaseLock();
        restore?.();
        controller.error(error);
      }
    },
    async cancel(reason) {
      try {
        await reader.cancel(reason);
      } finally {
        reader.releaseLock();
        restore?.();
      }
    },
  });
}
